import { readProjectSettings } from './adminSettings';
import { readAdminSession } from './authProvider';
import { pteScoreFromTrait } from '../pteScoreScale';

type AdminApiEnvelope<T> = {
    success?: boolean;
    data?: T;
    statusCode?: number;
    errorMessage?: string | null;
    message?: string;
    error?: string;
    title?: string;
    detail?: string;
};

type MockTestScoreReviewListDto = {
    totalCount: number;
    page: number;
    pageSize: number;
    items: MockTestScoreReviewAttemptDto[];
};

export type MockTestScoreReviewListParams = {
    page: number;
    pageSize: number;
    search?: string;
    dateFrom?: string;
    dateTo?: string;
    userEmail?: string;
    mockTestIds?: string[];
    userIds?: string[];
    mockTestTitle?: string;
    sectionCategory?: string;
    status?: string;
    sort?: string;
    order?: 'ASC' | 'DESC';
};

export type MockTestReviewDetailRequestDto = {
    mockTestAttemptId: string;
};

export type AdminRescoreMockTestAttemptRequestDto = {
    userId?: string;
    userEmail?: string;
    mockTestId: string;
    mockTestAttemptId: string;
};

export type AdminRescoreMockTestAttemptResponseDto = {
    userId: string;
    userEmail: string;
    scoreResult: {
        mockTestId: string;
        mockTestAttemptId: string;
        overallScore: number | null;
    };
};

export type JsonPrimitive = string | number | boolean | null;
export type JsonValue = JsonPrimitive | JsonObject | JsonValue[];
export type JsonObject = {
    [key: string]: JsonValue;
};

export type MockTestReviewAttemptDto = {
    id: string;
    mockTestId: string;
    mockTestAttemptId: string;
    mockTestTitle: string;
    examName: string;
    mockTestKind: string;
    status: string;
    attemptedAt: string;
    overallScore: number | null;
    totalTimeSpentSeconds: number;
    sections: MockTestReviewSectionDto[];
};

export type MockTestAudioTraceDto = {
    requestId: string;
    mockTestAttemptItemId: string;
    mockTestItemId: string;
    examTaskId: string;
    questionNo: string;
    sequenceNumber: number | null;
    examTaskType: string;
    examTaskCategory: string;
    examName: string;
    clientReportedRecording: boolean;
    clientRecordingDurationMs: number | null;
    clientRecordingStartedAt: string | null;
    clientRecordingStoppedAt: string | null;
    clientLastAction: string | null;
    clientAttachedFile: boolean | null;
    clientAttachedFileName: string | null;
    clientAttachedFileSizeBytes: number | null;
    clientAudioContentType: string | null;
    apiReceivedAt: string;
    apiReceivedFile: boolean;
    apiReceivedFileName: string | null;
    apiReceivedFileSizeBytes: number | null;
    apiReceivedAudioContentType: string | null;
    apiClientAudioDurationSeconds: number | null;
    sourceAudioUploadStatus: string | null;
    sourceAudioPath: string | null;
    sourceAudioUploadedAt: string | null;
    queuePublishedAt: string | null;
    queuedForPersistence: boolean | null;
    queuedForAudioProcessing: boolean | null;
    queuedForScoring: boolean | null;
    persistenceStatus: string | null;
    persistenceStartedAt: string | null;
    persistenceCompletedAt: string | null;
    persistenceErrorCode: string | null;
    persistenceErrorMessage: string | null;
    audioConsumerStatus: string | null;
    audioConsumerStartedAt: string | null;
    audioConsumerCompletedAt: string | null;
    normalizedAudioPath: string | null;
    normalizedAudioSizeBytes: number | null;
    audioConsumerErrorCode: string | null;
    audioConsumerErrorMessage: string | null;
    scoringStatus: string | null;
    scoringStartedAt: string | null;
    scoringCompletedAt: string | null;
    scoringUsedAudio: boolean | null;
    scoringNoAudioReason: string | null;
    scoringErrorCode: string | null;
    scoringErrorMessage: string | null;
    speechAnalysisStatus: string | null;
    speechAnalysisStartedAt: string | null;
    speechAnalysisCompletedAt: string | null;
    speechAudioDurationSeconds: number | null;
    speechTranscript: string | null;
    speechTranscriptPreview: string | null;
    speechTranscriptLength: number | null;
    speechTokenCount: number | null;
    speechPauseEventCount: number | null;
    scoreRaw: number | null;
    scoreMax: number | null;
    scoreSummary: string | null;
    scorePayloadSummary: string | null;
    answerAudioUrl: string | null;
};

export type MockTestReviewSectionDto = {
    id: string;
    mockTestSectionId: string;
    mockTestAttemptSectionId: string;
    sequenceNumber: number;
    sectionCategory: string;
    durationSeconds: number;
    remainingSeconds: number;
    timeSpentSeconds: number;
    taskGroups: MockTestReviewTaskGroupDto[];
};

export type MockTestReviewTaskGroupDto = {
    examTaskType: string;
    tasks: MockTestReviewTaskDto[];
};

export type MockTestReviewTaskDto = {
    id: string;
    mockTestItemId: string;
    mockTestAttemptItemId: string;
    examTaskId: string;
    sequenceNumber: number;
    status: string;
    questionText: string;
    userAnswer: string | null;
    answerAudioUrl: string | null;
    timeSpentSeconds: number;
    remainingSeconds: number | null;
    rawScore: number | null;
    maxScore: number | null;
    scorePercentage: number | null;
    startedAt: string | null;
    answeredAt: string | null;
    submittedAt: string | null;
    scoredAt: string | null;
    scoreResult: MarkingResponseDto | null;
    scoreMetrics: MockTestReviewScoreMetricDto[];
    examTask: PublishedMockTestExamTaskDto | null;
    userResponse: MockTestReviewUserResponseDto | null;
    scoredResponse: MockTestReviewScoredResponseDto | null;
};

export type MockTestReviewScoreMetricDto = {
    metricKey: string;
    metricLabel: string;
    score: number;
    outOf: number;
    scoreOutOf90: number;
    isLast: boolean;
};

export type MockTestReviewUserResponseDto = {
    answerText: string | null;
    answerAudioUrl: string | null;
    responsePayload: JsonValue | null;
};

export type MockTestReviewScoredResponseDto = {
    rawScore: number | null;
    maxScore: number | null;
    scorePercentage: number | null;
    scoredAt: string | null;
    transcript: string | null;
    scoreResult: MarkingResponseDto | null;
    scoreMetrics: MockTestReviewScoreMetricDto[];
};

export type PublishedMockTestExamTaskDto = {
    examTaskId: string;
    displayTitle: string;
    promptBody: string | null;
    complexityRank: string;
    examTaskType: string;
    isActive: boolean;
    isPremiumContent: boolean;
    mediaResourceUrl: string | null;
    mediaCategory: string | null;
    answerChoices: string | null;
    configSettings: ExamTaskConfigSettingsDto | null;
    createdAt: string;
    updatedAt: string;
    segments: PublishedMockTestExamTaskSegmentDto[];
    voices: PublishedMockTestExamTaskVoiceDto[];
};

export type PublishedMockTestExamTaskSegmentDto = {
    examTaskSegmentId: string;
    examTaskId: string;
    sequenceNumber: number | null;
    prefixText: string | null;
    suffixText: string | null;
    correctIndex: number | null;
    optionList: string | null;
    isCorrectFlag: boolean | null;
    isEnabled: boolean;
};

export type PublishedMockTestExamTaskVoiceDto = {
    examTaskMediaVoiceId: string;
    voiceLabel: string;
    genderCode: string;
    languageTag: string;
    contentEtag: string;
    storageFilename: string;
    durationSeconds: number;
    examTaskId: string | null;
};

export type ExamTaskConfigSettingsDto = {
    additional_options: string[];
};

export type MarkingResponseDto = {
    examTaskCategory: string | null;
    examTaskType: string | null;
    examName: string | null;
    examTaskId: string | null;
    audioDuration: number | null;
    choosenOption: ChoosenOptionDto[];
    writtenText: string | null;
    transcript: string | null;
    oralFluencyReview: OralFluencyReviewDto | null;
    contentCorrection: WordEditDto[];
    grammarCorrection: GrammerStrDto[];
    hiwResult: HighlightIncorrectWordResultDto[];
    segments: ExamTaskSegmentDto[];
    optionResults: OptionResultDto[];
    markTableResult: MarkTableDto[];
    promptBody: string | null;
    discoveryTags: string | null;
};

export type ChoosenOptionDto = {
    examTaskSegmentId: string;
    selectedValue: string | null;
    isSelected: boolean | null;
};

export type OptionResultDto = ChoosenOptionDto & {
    isCorrectAnswer: boolean | null;
};

export type OralFluencyReviewDto = {
    totalSeconds: number;
    pauseEventCount: number;
    transcript: string;
    tokenResults: ArticulationTokenResultDto[];
    tokenResultsHtml: string;
};

export type ArticulationTokenResultDto = {
    token: string;
    grade: string;
    hasBreak: boolean;
    tempo: string;
    breakQuality: string;
};

export type WordEditDto = {
    kind: string;
    text: string;
};

export type GrammerStrDto = {
    uniqueId: string;
    str: string;
    isTooltip: boolean;
    shortMessage: string | null;
    message: string | null;
    replacementStr: string | null;
    word: string | null;
};

export type HighlightIncorrectWordResultDto = {
    word: string;
    isSelected: boolean;
    isCorrect: boolean;
};

export type ExamTaskSegmentDto = {
    examTaskSegmentId: string;
    examTaskId: string;
    sequenceNumber: number | null;
    prefixText: string | null;
    suffixText: string | null;
    answerKey: string | null;
    correctIndex: number | null;
    optionList: string | null;
    isCorrectFlag: boolean | null;
    isEnabled: boolean;
};

export type MarkTableDto = {
    title: string;
    score: number;
    outOf: number;
    isLast: boolean;
    scoreOutOf90: number;
};

export type MockTestScoreReviewExamTaskDto = {
    examTaskId: string;
    examTaskType: string;
    examTaskCategory: string;
    questionText: string;
    userAnswer: string;
    score: number | null;
    systemFeedback: string | null;
};

export type MockTestScoreReviewAttemptDto = {
    mockTestAttemptId: string;
    userId: string;
    userName: string;
    examName: string;
    mockTestKind: string;
    status: string;
    attemptedAt: string;
    lastAttemptedAt: string | null;
    examTasks: MockTestScoreReviewExamTaskDto[];
};

function trimTrailingSlash(value: string): string {
    return value.replace(/\/+$/, '');
}

function trimSlashes(value: string): string {
    return value.replace(/^\/+|\/+$/g, '');
}

function hasVersionedApiPath(value: string): boolean {
    return /\/api\/v[^/]+$/i.test(value);
}

function getAdminApiConfig() {
    const settings = readProjectSettings();
    const session = readAdminSession();
    const baseUrl = trimTrailingSlash(
        String(
            import.meta.env.VITE_API_BASE_URL ??
                import.meta.env.VITE_API_URL ??
                settings.adminApiBaseUrl ??
                'https://test-api.pteultra.com/api/v1',
        ),
    );
    const adminToken = String(
        session?.adminToken ?? session?.accessToken ?? import.meta.env.VITE_ADMIN_TOKEN ?? settings.adminApiToken ?? '',
    );

    const apiVersion = String(import.meta.env.VITE_API_VERSION ?? settings.adminApiVersion ?? '1');

    return { baseUrl, apiVersion, adminToken };
}

function getAdminAssetBaseUrl(): string {
    const fileBaseUrl = trimTrailingSlash(String(import.meta.env.VITE_BASE_FILE_URL ?? ''));
    if (fileBaseUrl) {
        return fileBaseUrl;
    }

    const { baseUrl } = getAdminApiConfig();
    if (hasVersionedApiPath(baseUrl)) {
        return trimTrailingSlash(baseUrl.replace(/\/api\/v[^/]+$/i, ''));
    }

    return trimTrailingSlash(baseUrl);
}

function resolveAdminAssetUrl(value: unknown): string | null {
    const rawValue = String(value ?? '').trim();
    if (!rawValue) return null;

    if (/^https?:\/\//i.test(rawValue)) {
        return rawValue;
    }

    const assetBaseUrl = getAdminAssetBaseUrl();
    if (!assetBaseUrl) {
        return rawValue;
    }

    return new URL(rawValue.replace(/^\/+/, ''), `${assetBaseUrl}/`).toString();
}

function getErrorMessage(payload: unknown, fallback: string): string {
    if (typeof payload === 'string' && payload.trim()) return payload;
    if (payload && typeof payload === 'object') {
        const candidate = payload as Record<string, unknown>;
        const direct =
            candidate.errorMessage ?? candidate.message ?? candidate.error ?? candidate.title ?? candidate.detail;
        if (typeof direct === 'string' && direct.trim()) return direct;
    }

    return fallback;
}

function unwrapAdminApiPayload<T>(payload: T | AdminApiEnvelope<T>): T {
    if (!payload || typeof payload !== 'object' || !('data' in payload)) {
        return payload as T;
    }

    const envelope = payload as AdminApiEnvelope<T>;
    if (envelope.success === false) {
        throw new Error(getErrorMessage(envelope, 'Mock test score review request failed'));
    }

    return envelope.data !== undefined ? envelope.data : (payload as T);
}

function asObject(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : {};
}

function asObjectArray(value: unknown): Record<string, unknown>[] {
    return Array.isArray(value)
        ? (value.filter((entry) => entry && typeof entry === 'object' && !Array.isArray(entry)) as Record<string, unknown>[])
        : [];
}

function asNullableObject(value: unknown): Record<string, unknown> | null {
    return value && typeof value === 'object' && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : null;
}

function asString(value: unknown, fallback = ''): string {
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    return fallback;
}

function asNullableString(value: unknown): string | null {
    const normalized = asString(value);
    return normalized ? normalized : null;
}

function asNullableNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim()) {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
}

function asNumber(value: unknown, fallback = 0): number {
    return asNullableNumber(value) ?? fallback;
}

function asBoolean(value: unknown, fallback = false): boolean {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
        if (value.toLowerCase() === 'true') return true;
        if (value.toLowerCase() === 'false') return false;
    }
    return fallback;
}

function asNullableBoolean(value: unknown): boolean | null {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
        if (value.toLowerCase() === 'true') return true;
        if (value.toLowerCase() === 'false') return false;
    }
    return null;
}

function asJsonValue(value: unknown): JsonValue | null {
    if (value === null || value === undefined) return null;
    if (typeof value === 'string' || typeof value === 'boolean') return value;
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null;
    }
    if (Array.isArray(value)) {
        return value.map((entry) => asJsonValue(entry));
    }
    if (typeof value === 'object') {
        const result: JsonObject = {};
        for (const [key, entry] of Object.entries(value)) {
            result[key] = asJsonValue(entry);
        }
        return result;
    }

    return null;
}

function asStringArray(value: unknown): string[] {
    return Array.isArray(value)
        ? value.map((entry) => asString(entry)).filter(Boolean)
        : [];
}

function normalizeListExamTask(item: unknown, index: number): MockTestScoreReviewExamTaskDto {
    const record = asObject(item);

    return {
        examTaskId: asString(record.examTaskId, `exam-task-${index + 1}`),
        examTaskType: asString(record.examTaskType, 'Unknown'),
        examTaskCategory: asString(record.examTaskCategory),
        questionText: asString(record.questionText),
        userAnswer: asString(record.userAnswer),
        score: asNullableNumber(record.score),
        systemFeedback: asNullableString(record.systemFeedback),
    };
}

function normalizeListAttempt(item: unknown, index: number): MockTestScoreReviewAttemptDto {
    const record = asObject(item);

    return {
        mockTestAttemptId: asString(record.mockTestAttemptId, `attempt-${index + 1}`),
        userId: asString(record.userId),
        userName: asString(record.userName, 'Unknown User'),
        examName: asString(record.examName),
        mockTestKind: asString(record.mockTestKind),
        status: asString(record.status),
        attemptedAt: asString(record.attemptedAt),
        lastAttemptedAt: asNullableString(record.lastAttemptedAt),
        examTasks: asObjectArray(record.examTasks).map(normalizeListExamTask),
    };
}

function normalizeMockTestReviewScoreMetric(item: unknown): MockTestReviewScoreMetricDto {
    const record = asObject(item);

    return {
        metricKey: asString(record.metricKey),
        metricLabel: asString(record.metricLabel),
        score: asNumber(record.score),
        outOf: asNumber(record.outOf),
        scoreOutOf90: asNumber(record.scoreOutOf90),
        isLast: asBoolean(record.isLast),
    };
}

function normalizeChoosenOption(item: unknown): ChoosenOptionDto {
    const record = asObject(item);

    return {
        examTaskSegmentId: asString(record.examTaskSegmentId),
        selectedValue: asNullableString(record.selectedValue),
        isSelected: asNullableBoolean(record.isSelected),
    };
}

function normalizeOptionResult(item: unknown): OptionResultDto {
    const record = asObject(item);

    return {
        ...normalizeChoosenOption(item),
        isCorrectAnswer: asNullableBoolean(record.isCorrectAnswer),
    };
}

function normalizeArticulationTokenResult(item: unknown): ArticulationTokenResultDto {
    const record = asObject(item);

    return {
        token: asString(record.token),
        grade: asString(record.grade),
        hasBreak: asBoolean(record.hasBreak),
        tempo: asString(record.tempo),
        breakQuality: asString(record.breakQuality),
    };
}

function normalizeOralFluencyReview(item: unknown): OralFluencyReviewDto {
    const record = asObject(item);

    return {
        totalSeconds: asNumber(record.totalSeconds),
        pauseEventCount: asNumber(record.pauseEventCount),
        transcript: asString(record.transcript),
        tokenResults: asObjectArray(record.tokenResults).map(normalizeArticulationTokenResult),
        tokenResultsHtml: asString(record.tokenResultsHtml),
    };
}

function normalizeWordEdit(item: unknown): WordEditDto {
    const record = asObject(item);

    return {
        kind: asString(record.kind),
        text: asString(record.text),
    };
}

function normalizeGrammerStr(item: unknown): GrammerStrDto {
    const record = asObject(item);

    return {
        uniqueId: asString(record.uniqueId),
        str: asString(record.str),
        isTooltip: asBoolean(record.isTooltip),
        shortMessage: asNullableString(record.shortMessage),
        message: asNullableString(record.message),
        replacementStr: asNullableString(record.replacementStr),
        word: asNullableString(record.word),
    };
}

function normalizeHighlightIncorrectWordResult(item: unknown): HighlightIncorrectWordResultDto {
    const record = asObject(item);

    return {
        word: asString(record.word),
        isSelected: asBoolean(record.isSelected),
        isCorrect: asBoolean(record.isCorrect),
    };
}

function normalizeExamTaskSegment(item: unknown): ExamTaskSegmentDto {
    const record = asObject(item);

    return {
        examTaskSegmentId: asString(record.examTaskSegmentId),
        examTaskId: asString(record.examTaskId),
        sequenceNumber: asNullableNumber(record.sequenceNumber),
        prefixText: asNullableString(record.prefixText),
        suffixText: asNullableString(record.suffixText),
        answerKey: asNullableString(record.answerKey),
        correctIndex: asNullableNumber(record.correctIndex),
        optionList: asNullableString(record.optionList),
        isCorrectFlag: asNullableBoolean(record.isCorrectFlag),
        isEnabled: asBoolean(record.isEnabled),
    };
}

function normalizeMarkTable(item: unknown): MarkTableDto {
    const record = asObject(item);
    const score = asNumber(record.score);
    const outOf = asNumber(record.outOf);

    return {
        title: asString(record.title),
        score,
        outOf,
        isLast: asBoolean(record.isLast),
        scoreOutOf90: asNumber(
            record.scoreOutOf90,
            pteScoreFromTrait(score, 0, outOf) ?? 0,
        ),
    };
}

function normalizeMarkingResponse(item: unknown): MarkingResponseDto {
    const record = asObject(item);

    return {
        examTaskCategory: asNullableString(record.examTaskCategory),
        examTaskType: asNullableString(record.examTaskType),
        examName: asNullableString(record.examName),
        examTaskId: asNullableString(record.examTaskId),
        audioDuration: asNullableNumber(record.audioDuration),
        choosenOption: asObjectArray(record.choosenOption).map(normalizeChoosenOption),
        writtenText: asNullableString(record.writtenText),
        transcript: asNullableString(record.transcript),
        oralFluencyReview: asNullableObject(record.oralFluencyReview)
            ? normalizeOralFluencyReview(record.oralFluencyReview)
            : null,
        contentCorrection: asObjectArray(record.contentCorrection).map(normalizeWordEdit),
        grammarCorrection: asObjectArray(record.grammarCorrection).map(normalizeGrammerStr),
        hiwResult: asObjectArray(record.hiwResult).map(normalizeHighlightIncorrectWordResult),
        segments: asObjectArray(record.segments).map(normalizeExamTaskSegment),
        optionResults: asObjectArray(record.optionResults).map(normalizeOptionResult),
        markTableResult: asObjectArray(record.markTableResult).map(normalizeMarkTable),
        promptBody: asNullableString(record.promptBody),
        discoveryTags: asNullableString(record.discoveryTags),
    };
}

function normalizeMockTestReviewUserResponse(item: unknown): MockTestReviewUserResponseDto {
    const record = asObject(item);

    return {
        answerText: asNullableString(record.answerText),
        answerAudioUrl: resolveAdminAssetUrl(record.answerAudioUrl),
        responsePayload: asJsonValue(record.responsePayload),
    };
}

function normalizeMockTestReviewScoredResponse(item: unknown): MockTestReviewScoredResponseDto {
    const record = asObject(item);

    return {
        rawScore: asNullableNumber(record.rawScore),
        maxScore: asNullableNumber(record.maxScore),
        scorePercentage: asNullableNumber(record.scorePercentage),
        scoredAt: asNullableString(record.scoredAt),
        transcript: asNullableString(record.transcript),
        scoreResult: asNullableObject(record.scoreResult) ? normalizeMarkingResponse(record.scoreResult) : null,
        scoreMetrics: asObjectArray(record.scoreMetrics).map(normalizeMockTestReviewScoreMetric),
    };
}

function normalizePublishedMockTestExamTaskSegment(item: unknown): PublishedMockTestExamTaskSegmentDto {
    const record = asObject(item);

    return {
        examTaskSegmentId: asString(record.examTaskSegmentId),
        examTaskId: asString(record.examTaskId),
        sequenceNumber: asNullableNumber(record.sequenceNumber),
        prefixText: asNullableString(record.prefixText),
        suffixText: asNullableString(record.suffixText),
        correctIndex: asNullableNumber(record.correctIndex),
        optionList: asNullableString(record.optionList),
        isCorrectFlag: asNullableBoolean(record.isCorrectFlag),
        isEnabled: asBoolean(record.isEnabled),
    };
}

function normalizePublishedMockTestExamTaskVoice(item: unknown): PublishedMockTestExamTaskVoiceDto {
    const record = asObject(item);

    return {
        examTaskMediaVoiceId: asString(record.examTaskMediaVoiceId),
        voiceLabel: asString(record.voiceLabel),
        genderCode: asString(record.genderCode),
        languageTag: asString(record.languageTag),
        contentEtag: asString(record.contentEtag),
        storageFilename: asString(record.storageFilename),
        durationSeconds: asNumber(record.durationSeconds),
        examTaskId: asNullableString(record.examTaskId),
    };
}

function normalizeExamTaskConfigSettings(item: unknown): ExamTaskConfigSettingsDto {
    const record = asObject(item);

    return {
        additional_options: asStringArray(record.additional_options ?? record.additionalOptions),
    };
}

function normalizePublishedMockTestExamTask(item: unknown): PublishedMockTestExamTaskDto {
    const record = asObject(item);

    return {
        examTaskId: asString(record.examTaskId),
        displayTitle: asString(record.displayTitle),
        promptBody: asNullableString(record.promptBody),
        complexityRank: asString(record.complexityRank),
        examTaskType: asString(record.examTaskType),
        isActive: asBoolean(record.isActive),
        isPremiumContent: asBoolean(record.isPremiumContent),
        mediaResourceUrl: resolveAdminAssetUrl(record.mediaResourceUrl),
        mediaCategory: asNullableString(record.mediaCategory),
        answerChoices: asNullableString(record.answerChoices),
        configSettings: asNullableObject(record.configSettings)
            ? normalizeExamTaskConfigSettings(record.configSettings)
            : null,
        createdAt: asString(record.createdAt),
        updatedAt: asString(record.updatedAt),
        segments: asObjectArray(record.segments).map(normalizePublishedMockTestExamTaskSegment),
        voices: asObjectArray(record.voices).map(normalizePublishedMockTestExamTaskVoice),
    };
}

function normalizeMockTestReviewTask(item: unknown, index: number): MockTestReviewTaskDto {
    const record = asObject(item);

    return {
        id: asString(record.id, asString(record.mockTestAttemptItemId, `review-task-${index + 1}`)),
        mockTestItemId: asString(record.mockTestItemId),
        mockTestAttemptItemId: asString(record.mockTestAttemptItemId),
        examTaskId: asString(record.examTaskId),
        sequenceNumber: asNumber(record.sequenceNumber, index + 1),
        status: asString(record.status),
        questionText: asString(record.questionText),
        userAnswer: asNullableString(record.userAnswer),
        answerAudioUrl: resolveAdminAssetUrl(record.answerAudioUrl),
        timeSpentSeconds: asNumber(record.timeSpentSeconds),
        remainingSeconds: asNullableNumber(record.remainingSeconds),
        rawScore: asNullableNumber(record.rawScore),
        maxScore: asNullableNumber(record.maxScore),
        scorePercentage: asNullableNumber(record.scorePercentage),
        startedAt: asNullableString(record.startedAt),
        answeredAt: asNullableString(record.answeredAt),
        submittedAt: asNullableString(record.submittedAt),
        scoredAt: asNullableString(record.scoredAt),
        scoreResult: asNullableObject(record.scoreResult) ? normalizeMarkingResponse(record.scoreResult) : null,
        scoreMetrics: asObjectArray(record.scoreMetrics).map(normalizeMockTestReviewScoreMetric),
        examTask: asNullableObject(record.examTask) ? normalizePublishedMockTestExamTask(record.examTask) : null,
        userResponse: asNullableObject(record.userResponse)
            ? normalizeMockTestReviewUserResponse(record.userResponse)
            : null,
        scoredResponse: asNullableObject(record.scoredResponse)
            ? normalizeMockTestReviewScoredResponse(record.scoredResponse)
            : null,
    };
}

function normalizeMockTestReviewTaskGroup(item: unknown): MockTestReviewTaskGroupDto {
    const record = asObject(item);

    return {
        examTaskType: asString(record.examTaskType),
        tasks: asObjectArray(record.tasks).map(normalizeMockTestReviewTask),
    };
}

function normalizeMockTestReviewSection(item: unknown, index: number): MockTestReviewSectionDto {
    const record = asObject(item);

    return {
        id: asString(record.id, asString(record.mockTestAttemptSectionId, `review-section-${index + 1}`)),
        mockTestSectionId: asString(record.mockTestSectionId),
        mockTestAttemptSectionId: asString(record.mockTestAttemptSectionId),
        sequenceNumber: asNumber(record.sequenceNumber, index + 1),
        sectionCategory: asString(record.sectionCategory),
        durationSeconds: asNumber(record.durationSeconds),
        remainingSeconds: asNumber(record.remainingSeconds),
        timeSpentSeconds: asNumber(record.timeSpentSeconds),
        taskGroups: asObjectArray(record.taskGroups).map(normalizeMockTestReviewTaskGroup),
    };
}

function normalizeMockTestReviewAttempt(item: unknown): MockTestReviewAttemptDto {
    const record = asObject(item);

    return {
        id: asString(record.id, asString(record.mockTestAttemptId)),
        mockTestId: asString(record.mockTestId),
        mockTestAttemptId: asString(record.mockTestAttemptId),
        mockTestTitle: asString(record.mockTestTitle),
        examName: asString(record.examName),
        mockTestKind: asString(record.mockTestKind),
        status: asString(record.status),
        attemptedAt: asString(record.attemptedAt),
        overallScore: asNullableNumber(record.overallScore),
        totalTimeSpentSeconds: asNumber(record.totalTimeSpentSeconds),
        sections: asObjectArray(record.sections).map(normalizeMockTestReviewSection),
    };
}

function normalizeMockTestAudioTrace(item: unknown): MockTestAudioTraceDto {
    const record = asObject(item);

    return {
        requestId: asString(record.requestId),
        mockTestAttemptItemId: asString(record.mockTestAttemptItemId),
        mockTestItemId: asString(record.mockTestItemId),
        examTaskId: asString(record.examTaskId),
        questionNo: asString(record.questionNo),
        sequenceNumber: asNullableNumber(record.sequenceNumber),
        examTaskType: asString(record.examTaskType),
        examTaskCategory: asString(record.examTaskCategory),
        examName: asString(record.examName),
        clientReportedRecording: asBoolean(record.clientReportedRecording),
        clientRecordingDurationMs: asNullableNumber(record.clientRecordingDurationMs),
        clientRecordingStartedAt: asNullableString(record.clientRecordingStartedAt),
        clientRecordingStoppedAt: asNullableString(record.clientRecordingStoppedAt),
        clientLastAction: asNullableString(record.clientLastAction),
        clientAttachedFile: asNullableBoolean(record.clientAttachedFile),
        clientAttachedFileName: asNullableString(record.clientAttachedFileName),
        clientAttachedFileSizeBytes: asNullableNumber(record.clientAttachedFileSizeBytes),
        clientAudioContentType: asNullableString(record.clientAudioContentType),
        apiReceivedAt: asString(record.apiReceivedAt),
        apiReceivedFile: asBoolean(record.apiReceivedFile),
        apiReceivedFileName: asNullableString(record.apiReceivedFileName),
        apiReceivedFileSizeBytes: asNullableNumber(record.apiReceivedFileSizeBytes),
        apiReceivedAudioContentType: asNullableString(record.apiReceivedAudioContentType),
        apiClientAudioDurationSeconds: asNullableNumber(record.apiClientAudioDurationSeconds),
        sourceAudioUploadStatus: asNullableString(record.sourceAudioUploadStatus),
        sourceAudioPath: asNullableString(record.sourceAudioPath),
        sourceAudioUploadedAt: asNullableString(record.sourceAudioUploadedAt),
        queuePublishedAt: asNullableString(record.queuePublishedAt),
        queuedForPersistence: asNullableBoolean(record.queuedForPersistence),
        queuedForAudioProcessing: asNullableBoolean(record.queuedForAudioProcessing),
        queuedForScoring: asNullableBoolean(record.queuedForScoring),
        persistenceStatus: asNullableString(record.persistenceStatus),
        persistenceStartedAt: asNullableString(record.persistenceStartedAt),
        persistenceCompletedAt: asNullableString(record.persistenceCompletedAt),
        persistenceErrorCode: asNullableString(record.persistenceErrorCode),
        persistenceErrorMessage: asNullableString(record.persistenceErrorMessage),
        audioConsumerStatus: asNullableString(record.audioConsumerStatus),
        audioConsumerStartedAt: asNullableString(record.audioConsumerStartedAt),
        audioConsumerCompletedAt: asNullableString(record.audioConsumerCompletedAt),
        normalizedAudioPath: resolveAdminAssetUrl(record.normalizedAudioPath),
        normalizedAudioSizeBytes: asNullableNumber(record.normalizedAudioSizeBytes),
        audioConsumerErrorCode: asNullableString(record.audioConsumerErrorCode),
        audioConsumerErrorMessage: asNullableString(record.audioConsumerErrorMessage),
        scoringStatus: asNullableString(record.scoringStatus),
        scoringStartedAt: asNullableString(record.scoringStartedAt),
        scoringCompletedAt: asNullableString(record.scoringCompletedAt),
        scoringUsedAudio: asNullableBoolean(record.scoringUsedAudio),
        scoringNoAudioReason: asNullableString(record.scoringNoAudioReason),
        scoringErrorCode: asNullableString(record.scoringErrorCode),
        scoringErrorMessage: asNullableString(record.scoringErrorMessage),
        speechAnalysisStatus: asNullableString(record.speechAnalysisStatus),
        speechAnalysisStartedAt: asNullableString(record.speechAnalysisStartedAt),
        speechAnalysisCompletedAt: asNullableString(record.speechAnalysisCompletedAt),
        speechAudioDurationSeconds: asNullableNumber(record.speechAudioDurationSeconds),
        speechTranscript: asNullableString(record.speechTranscript),
        speechTranscriptPreview: asNullableString(record.speechTranscriptPreview),
        speechTranscriptLength: asNullableNumber(record.speechTranscriptLength),
        speechTokenCount: asNullableNumber(record.speechTokenCount),
        speechPauseEventCount: asNullableNumber(record.speechPauseEventCount),
        scoreRaw: asNullableNumber(record.scoreRaw),
        scoreMax: asNullableNumber(record.scoreMax),
        scoreSummary: asNullableString(record.scoreSummary),
        scorePayloadSummary: asNullableString(record.scorePayloadSummary),
        answerAudioUrl: resolveAdminAssetUrl(record.answerAudioUrl),
    };
}

function normalizeAdminRescoreMockTestAttemptResponse(item: unknown): AdminRescoreMockTestAttemptResponseDto {
    const record = asObject(item);
    const scoreResult = asObject(record.scoreResult);

    return {
        userId: asString(record.userId),
        userEmail: asString(record.userEmail),
        scoreResult: {
            mockTestId: asString(scoreResult.mockTestId),
            mockTestAttemptId: asString(scoreResult.mockTestAttemptId),
            overallScore: asNullableNumber(scoreResult.overallScore),
        },
    };
}

async function requestAdminMockTestScoreReview<T>(
    path: string,
    init: RequestInit = {},
    query?: Record<string, string | string[] | undefined>,
): Promise<T> {
    const { baseUrl, adminToken } = getAdminApiConfig();
    const url = new URL(`/api/admin/mock-test-score-review/${trimSlashes(path)}`, baseUrl);

    for (const [key, value] of Object.entries(query ?? {})) {
        if (Array.isArray(value)) {
            for (const entry of value) {
                if (entry) {
                    url.searchParams.append(key, entry);
                }
            }
            continue;
        }

        if (value) {
            url.searchParams.set(key, value);
        }
    }

    const headers = new Headers(init.headers);
    if (!headers.has('Accept')) {
        headers.set('Accept', 'application/json');
    }
    if (init.body && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
    }
    if (adminToken && !headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${adminToken}`);
    }

    const response = await fetch(url.toString(), {
        ...init,
        headers,
    });

    const contentType = response.headers.get('content-type') ?? '';
    const payload = contentType.includes('application/json') ? await response.json() : await response.text();

    if (!response.ok) {
        throw new Error(getErrorMessage(payload, `${response.status} ${response.statusText}`));
    }

    return payload as T;
}

async function requestAdminMockTest<T>(path: string, init: RequestInit = {}): Promise<T> {
    const { baseUrl, apiVersion, adminToken } = getAdminApiConfig();
    const apiRoot = hasVersionedApiPath(baseUrl) ? baseUrl : `${baseUrl}/api/v${apiVersion}`;
    const url = new URL(`${apiRoot}/AdminMockTest/${trimSlashes(path)}`);

    const headers = new Headers(init.headers);
    if (!headers.has('Accept')) {
        headers.set('Accept', 'application/json');
    }
    if (init.body && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
    }
    if (adminToken && !headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${adminToken}`);
    }

    const response = await fetch(url.toString(), {
        ...init,
        headers,
    });

    const contentType = response.headers.get('content-type') ?? '';
    const payload = contentType.includes('application/json') ? await response.json() : await response.text();

    if (!response.ok) {
        throw new Error(getErrorMessage(payload, `${response.status} ${response.statusText}`));
    }

    return payload as T;
}

export async function getMockTestScoreReviewList(params: MockTestScoreReviewListParams) {
    const payload = unwrapAdminApiPayload(
        await requestAdminMockTestScoreReview<MockTestScoreReviewListDto | AdminApiEnvelope<MockTestScoreReviewListDto>>(
            'get-list',
            { method: 'GET' },
            {
                pageNo: String(params.page),
                pageSize: String(params.pageSize),
                page: String(params.page),
                perPage: String(params.pageSize),
                q: params.search,
                search: params.search,
                dateFrom: params.dateFrom,
                dateTo: params.dateTo,
                userEmail: params.userEmail,
                mockTestIds: params.mockTestIds,
                userIds: params.userIds,
                mockTestTitle: params.mockTestTitle,
                sectionCategory: params.sectionCategory,
                status: params.status,
                sort: params.sort,
                order: params.order,
            },
        ),
    );

    return {
        data: asObjectArray(payload.items).map(normalizeListAttempt),
        total: asNumber(payload.totalCount),
        page: asNumber(payload.page, params.page),
        pageSize: asNumber(payload.pageSize, params.pageSize),
    };
}

export async function getMockTestReviewDetail(params: MockTestReviewDetailRequestDto) {
    const payload = unwrapAdminApiPayload(
        await requestAdminMockTestScoreReview<MockTestReviewAttemptDto | AdminApiEnvelope<MockTestReviewAttemptDto>>(
            `get-review-detail/${trimSlashes(params.mockTestAttemptId)}`,
            { method: 'GET' },
        ),
    );

    return {
        data: normalizeMockTestReviewAttempt(payload),
    };
}

export async function getMockTestAudioTraces(params: MockTestReviewDetailRequestDto) {
    const payload = unwrapAdminApiPayload(
        await requestAdminMockTestScoreReview<MockTestAudioTraceDto[] | AdminApiEnvelope<MockTestAudioTraceDto[]>>(
            `get-audio-traces/${trimSlashes(params.mockTestAttemptId)}`,
            { method: 'GET' },
        ),
    );

    return {
        data: asObjectArray(payload).map(normalizeMockTestAudioTrace),
    };
}

export async function rescoreMockTestAttempt(params: AdminRescoreMockTestAttemptRequestDto) {
    const payload = unwrapAdminApiPayload(
        await requestAdminMockTest<AdminRescoreMockTestAttemptResponseDto | AdminApiEnvelope<AdminRescoreMockTestAttemptResponseDto>>(
            'rescore-attempt',
            {
                method: 'POST',
                body: JSON.stringify({
                    userId: params.userId?.trim() || undefined,
                    userEmail: params.userEmail?.trim() || undefined,
                    mockTestId: params.mockTestId.trim(),
                    mockTestAttemptId: params.mockTestAttemptId.trim(),
                }),
            },
        ),
    );

    return normalizeAdminRescoreMockTestAttemptResponse(payload);
}
